<template>
  <div class="company-wall-municipality-card">
    <NuxtLink
      class="company-wall-municipality-card__link"
      target="_blank"
      :to="companyToUrl(company)"
    />

    <SCompanyCard
      v-bind="omit(company.companyCard, ['description', 'id', 'slug'])"
      :has-active-campaign-for-municipality="
        company.hasActiveCampaignForMunicipality
      "
    />

    <div
      class="company-wall-municipality-card__description"
      v-html="company.companyCard.description"
    />

    <div class="company-wall-municipality-card__buttons">
      <SButton
        v-if="productFormUrl"
        color="accent"
        prefix-icon="far fa-file-lines"
        :to="productFormUrl"
      >
        {{ tr("main_site.company_wall.cta.open_direct_match_form") }}
      </SButton>

      <SButton
        action-type="secondary"
        color="primary"
        fluid
        opaque
        suffix-icon="far fa-arrow-right"
        tabindex="-1"
        :tag="NuxtLink"
        target="_blank"
        :to="companyToUrl(company)"
      >
        {{ tr("main_site.company_wall.go_to_profile") }}
      </SButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { NuxtLink } from "#components";
import { omit } from "radash";
import { computed } from "vue";

import { SButton } from "@solvari/common-fe";
import { SCompanyCard } from "@solvari/components";
import { useI18nMainSiteCompanyWall } from "@solvari/translations";

import type { CompanyCardCompany } from "~/features/company-wall/api/localSearch.api.ts";

import { ziggy } from "@/shared/libs/ziggy";
import { buildCompanyWallUrl } from "~/features/company-wall/companyWallUrl.ts";

const props = defineProps<{
  company: CompanyCardCompany;
  geoProductSlug: string;
}>();

const { tr } = useI18nMainSiteCompanyWall();

function companyToUrl(company: CompanyCardCompany) {
  return buildCompanyWallUrl({
    companySlug: company.companyCard.slug,
    isSupportedLocale: company.isSupportedLocale,
    geoProductSlug: props.geoProductSlug,
    provinceSlug: company.provinceSlug,
    municipalitySlug: company.municipalitySlug,
    subMunicipalitySlug: company.subMunicipalitySlug,
  });
}

const productFormUrl = computed(() =>
  ziggy(
    "mainSite.geoProduct.leadForm",
    { formSlug: undefined, geoProductSlug: props.geoProductSlug },
    { customerId: props.company.companyCard.id },
  ),
);
</script>

<style lang="postcss">
.company-wall-municipality-card {
  @apply relative flex flex-col rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition-colors;

  &:hover {
    @apply border-primary-500 shadow;
  }

  &__link {
    @apply absolute inset-0 z-[1];
  }

  &__description {
    @apply mt-1 w-full grow whitespace-pre-line leading-7 text-gray-500;
  }

  &__buttons {
    @apply relative z-10 mt-2 grid gap-2 grid-cols-auto-40 grid-cols-auto-fit grid-cols-auto-grow;
  }
}
</style>
